import { useEffect, useMemo, useState } from 'react';

// Components
import { ConfigContext, Standings } from '@velinsi/smp-widgets-ui';

// Hooks
import useStageList from '../../hooks/useStageList';
import useCompetition from '../../hooks/useCompetition';
import useStage from '../../hooks/useStage';

// Utils
import {
  defaultHandleEventClick,
  defaultHandleTeamClick,
  defaultHandleTournamentClick,
  getUniqueListBy,
} from '@sportal365/smp-widget-utils';

// Constants
import { Sports, TableVariants } from '../../../../../packages/constants/sportType';

// Types
import { BaseWidgetProps, INormalizedKnockoutData, INormalizedStandingsGroup, IStandingsConfig } from '../../../../../packages/types';

// Misc
import { normalizeFootballCompetitionResponseData } from '../../normalizers/normalizeFootballCompetitionResponseData';
import { normalizeFootballStageResponseData } from '../../normalizers/normalizeFootballStageResponseData';
import ThemeProvider from '../../../../../packages/theme/ThemeProvider';
import useResizeObserver from '../../../../../packages/hooks/useResizeObserver';
import { checkOrGetDesktopDefaultDataElements, checkOrGetMobileDefaultDataElements } from '../../helpers/standings';
import { STANDINGS_DATA_ELEMENTS_BREAKPOINT } from '../../../../../packages/constants/generic';
import useRootNodeRef from '../../../../../packages/hooks/useRootNodeRef';

export type StandingsWidgetProps = BaseWidgetProps<IStandingsConfig>;

export const StandingsWidget = ({ themes, config, subscribers, shadow, sdkOptions, cuid }: StandingsWidgetProps) => {
  const [competitionId, setCompetitionId] = useState<string | undefined>(config?.dataCompetition);
  const [seasonId, setSeasonId] = useState<string | undefined>(config?.dataSeason);
  const [stageId, setStageId] = useState<string | undefined>(config?.dataStage);
  const { ref } = useRootNodeRef();

  const { stageList, resetStageList } = useStageList({ stageId, seasonId, competitionId });

  const {
    showWidget,
    handleClick,
    widgetData: { stage, rules, isLoading },
  } = useStage({
    subscribers,
    stageId,
    seasonId,
    competitionId,
    dataWidgetId: config?.dataWidgetId,
    dataWidgetSport: config?.dataWidgetSport,
    dataRefreshTime: config?.dataRefreshTime,
    dataGroups: config?.dataGroups,
    dataWidgetCuid: cuid,
    dataOffset: config?.dataOffset,
    dataLimit: config?.dataLimit,
  });

  const uniqueRulesByType = useMemo(() => getUniqueListBy('type', rules), [rules]);

  const [isMobile, setIsMobile] = useState(false);
  const measuredRef = useResizeObserver((fieldElement) => setIsMobile(fieldElement.offsetWidth < STANDINGS_DATA_ELEMENTS_BREAKPOINT));

  const normalizedStage = useMemo(
    () =>
      normalizeFootballStageResponseData({
        stage,
        dataEntityLinks: config?.dataEntityLinks,
        dataLabels: config?.dataLabels,
        dataTeamNameType: config?.dataTeamNameType,
        dataElements: isMobile
          ? checkOrGetMobileDefaultDataElements(config?.dataElementsMobile)
          : checkOrGetDesktopDefaultDataElements(config?.dataElementsDesktop),
        displayImage: config?.dataImageDisplay,
        isMobile,
        dataHighlightEntities: config?.dataHighlightEntities,
      }),
    [stage, config, isMobile],
  );

  useEffect(() => {
    if (!seasonId && stage && stage.tournamentSeasonId) {
      // If there is no seasonId, but there is stage, infer season from stage
      setSeasonId(stage.tournamentSeasonId);
    }
  }, [seasonId, stage]);

  useEffect(() => {
    if (!competitionId && stage && stage.tournamentId) {
      // If there is no competition, but there is stage, infer competition from stage
      setCompetitionId(stage.tournamentId);
    }
  }, [competitionId, stage]);

  const { competition } = useCompetition({ competitionId });
  const normalizedCompetition = useMemo(
    () => normalizeFootballCompetitionResponseData({ competition, dataEntityLinks: config?.dataEntityLinks }),
    [competition, config],
  );

  if (!showWidget) return null;

  const handleTeamClick = (id: string | number, name: string) => defaultHandleTeamClick(handleClick, { id, name });

  const handleTournamentClick = () => defaultHandleTournamentClick(handleClick, Number(competitionId), normalizedCompetition?.name);

  const handleEventClick = (event: string | number) => defaultHandleEventClick(handleClick, Number(event));

  const handleSeasonChange = (value?: string) => {
    setSeasonId(String(value));
    setStageId(undefined);
    resetStageList();
  };

  const handleActiveStageChange = (stageId: string) => setStageId(stageId);

  return (
    <ConfigContext.Provider value={{ ...config, sdkOptions }}>
      <ThemeProvider
        isLoading={isLoading}
        slickStyles={true}
        shadow={shadow}
        themes={themes}
        dataTheme={config?.dataTheme}
        css={{ backgroundColor: '$surfaceBackgroundColor' }}
        refForShadow={ref}
      >
        <Standings
          measuredRef={measuredRef}
          sport={Sports.FOOTBALL}
          stageId={stageId}
          seasonId={seasonId}
          stageList={stageList}
          rules={uniqueRulesByType}
          competition={normalizedCompetition}
          standings={normalizedStage?.standings as INormalizedStandingsGroup[]}
          dataLabels={config?.dataLabels}
          dataHeaderDisplay={config?.dataHeaderDisplay}
          dataOptionsDisplay={config?.dataOptionsDisplay}
          knockout={normalizedStage?.knockout as INormalizedKnockoutData}
          handleTeamClick={handleTeamClick}
          handleEventClick={handleEventClick}
          handleSeasonChange={handleSeasonChange}
          handleTournamentClick={handleTournamentClick}
          handleActiveStageChange={handleActiveStageChange}
          handleSubscriptionClick={handleClick}
          isMobile={isMobile}
          mobileDropdown={true}
          displayImage={config?.dataImageDisplay}
          tableVariant={TableVariants.STANDINGS}
        />
      </ThemeProvider>
    </ConfigContext.Provider>
  );
};
